/* eslint-disable react/prop-types */
/* eslint-disable no-prototype-builtins */
import PropTypes from 'prop-types'
import React, { createRef, useEffect, useState } from 'react'
import { FormControl, InputGroup } from 'react-bootstrap'
import { FaChevronLeft, FaSearch } from 'react-icons/fa'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import styled from 'styled-components'

import { useAppSelector } from 'appState/store'
import { IMultiLevelDropdown, ISelectedMenu, LabelCount } from './DropdownMultiLevel'
import MenuItem from './MenuItem'

interface IProps {
  data: IMultiLevelDropdown[]
  animation: any
  showMenuItems: boolean
  onClick: any
  closeMenu: any
  selectedMenu: ISelectedMenu
  changeMenuItem: (val: ISelectedMenuItem) => void
  labelCount: LabelCount
  dropdownLabel?: IDropdownLabel
}

export interface IDropdownLabel {
  title: string
  searchPlaceholder_1: string
  searchPlaceholder_2: string
}

export interface ISelectedMenuItem {
  prev: any
  current: any
}

const MenuItems = ({
  data,
  animation,
  showMenuItems,
  onClick,
  closeMenu,
  selectedMenu,
  changeMenuItem,
  labelCount,
  dropdownLabel,
}: IProps) => {
  const menuItemsRef = createRef<any>()

  const [itemsToShow, setItemsToShow] = useState(mapInitialDataToShow(data))
  const [filteredItem, setFilteredItem] = useState(mapInitialDataToShow(data))
  const [move, changeMove] = useState('prev')
  const [id, setId] = useState(null)
  const currentCustomer = useAppSelector((state) => state.auth.currentCustomer)
  const setCustomer = data.find((item) => item.id === currentCustomer)
  const [selectedItem, setSelectedItem] = useState<IMultiLevelDropdown>(
    setCustomer ? setCustomer : { id: '', value: dropdownLabel?.title || 'Select Customer', items: data },
  )

  function mapInitialDataToShow(data: IMultiLevelDropdown[]) {
    if (data.length > 1) {
      return data
    }
    return data.flatMap((items) => {
      return items.items.map((item) => ({ id: item.id, value: item.value, items: [] }))
    })
  }

  const changeMenuItems = (newItemsToShow: any, updatedMove: any, updatedId: any) => {
    setItemsToShow(newItemsToShow)
    setFilteredItem(newItemsToShow)
    changeMove(updatedMove)
    setId(updatedId)
  }

  useEffect(() => {
    changeMenuItems(
      selectedItem.items.length > 0 ? selectedItem.items : mapInitialDataToShow(data),
      'prev',
      Math.random().toString(36).substring(2, 9),
    )
  }, [data])

  const searchText = (text: string) => {
    if (!text) {
      setItemsToShow(filteredItem)
      return
    }
    const tenants = filteredItem.filter((tenant: any) => tenant.value.toLowerCase().includes(text.toLowerCase()))
    setItemsToShow(tenants)
  }

  const moveToNext = (targetItem: IMultiLevelDropdown) => {
    if (targetItem.items && targetItem.items.length > 0) {
      const newItems = itemsToShow.find((item: IMultiLevelDropdown) => item.id === targetItem.id)

      if (newItems !== undefined && 'items' in newItems && newItems.items!.length > 0) {
        setSelectedItem(targetItem)
        changeMenuItems(newItems.items, 'next', Math.random().toString(36).substr(2, 9))
      }
    } else if (onClick) {
      onClick(targetItem, closeMenu)
    }
  }

  const moveToPrevious = () => {
    setSelectedItem({ id: '', value: dropdownLabel?.title || 'Select Customer', items: data })
    changeMenuItems(mapInitialDataToShow(data), 'prev', Math.random().toString(36).substring(2, 9))
  }

  const setSelectedMenu = (value: any) => {
    changeMenuItem({ prev: selectedItem, current: value })
  }

  const childFactoryCreator = (classNames: any) => (child: any) => React.cloneElement(child, { classNames })

  const searchPlaceholder =
    data.length === 1
      ? dropdownLabel?.searchPlaceholder_2 || 'Search Practices'
      : selectedItem.value.toLowerCase() === dropdownLabel?.title.toLowerCase()
      ? dropdownLabel?.searchPlaceholder_1 || 'Search customers'
      : dropdownLabel?.searchPlaceholder_2 || 'Search Practices'

  return (
    <TransitionGroup childFactory={childFactoryCreator(move === 'next' ? animation[1] : animation[0])}>
      <CSSTransition nodeRef={menuItemsRef} timeout={10} key={id}>
        <Div
          className={`menuItems ${showMenuItems ? 'showMenuItems' : 'hideMenuItems'}`}
          ref={menuItemsRef}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="back" key={Math.random().toString(36).substring(2, 9)} onClick={moveToPrevious}>
            {selectedItem.value.toLowerCase() !== dropdownLabel?.title.toLowerCase() && (
              <p className="backArrow">
                <FaChevronLeft />
              </p>
            )}
            <p className="backButton">{data.length === 1 ? data[0].value : selectedItem.value}</p>
          </div>
          <InputGroup className="search-box">
            <FaSearch className="search-icon" />
            <FormControl
              className="search"
              placeholder={searchPlaceholder}
              onChange={(e) => searchText(e.target.value)}
            />
          </InputGroup>

          {itemsToShow.length <= 0 ? (
            <div style={{ fontSize: '0.875rem' }} className="text-center mt-1">
              Not found
            </div>
          ) : (
            itemsToShow.map((item: any) => (
              <MenuItem
                key={item.id}
                item={item}
                moveToNext={moveToNext}
                nextValue={item.hasOwnProperty('items') && item.items.length > 0}
                activeItem={selectedMenu.activeId}
                selectedMenuItem={setSelectedMenu}
                labelCount={labelCount}
              />
            ))
          )}
        </Div>
      </CSSTransition>
    </TransitionGroup>
  )
}

const Div = styled.div`
  position: absolute;
  /* perspective: 1000;
  transform-style: preserve-3d; */
  display: flex;
  flex-direction: column;
  z-index: 102;
  right: 0;
  width: auto;
  min-width: 15rem;
  height: auto;
  max-height: 25rem;
  overflow: auto;
  overflow-x: hidden;
  border-radius: 8px;
  margin-top: 0.1875rem;
  padding-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.neutral.neutral};
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.04);
  border: solid 1px ${({ theme }) => theme.colors.grey.lighter};
  background-color: ${({ theme }) => theme.colors.neutral.lightest};
  .search-box {
    margin: 0 1rem 0.5rem;
    width: auto;
    .search-icon {
      position: absolute;
      width: 0.75rem;
      height: 0.75rem;
      left: 0.75rem;
      z-index: 99;
      top: 0.6rem;
      color: ${({ theme }) => theme.colors.grey.neutral};
    }
    .search {
      ${({ theme }) => theme.typography.s.normal}
      color: ${({ theme }) => theme.colors.grey.neutral};
      height: 2rem;
      width: auto;
      padding-left: 2rem;
      border-radius: 8px !important;
    }
    .search:focus {
      color: ${({ theme }) => theme.colors.neutral.neutral};
    }
  }
  .back {
    display: flex;
    cursor: pointer;
    padding: 1rem;

    &:hover {
      background-color: ${({ theme }) => theme.colors.neutral.lightest};
    }

    .backButton {
      ${({ theme }) => theme.typography.s.medium}
    }

    .backArrow {
      color: ${({ theme }) => theme.colors.info.neutral};
      position: relative;
      left: -0.5rem;
      margin-left: 0.5rem;
    }
  }

  .menuItem {
  }
`

MenuItems.defaultProps = {
  onClick: null,
}

MenuItems.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  animation: PropTypes.arrayOf(PropTypes.string).isRequired,
  showMenuItems: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
  closeMenu: PropTypes.func,
}

export default MenuItems
